import {
	Component,
	EventListener,
	getComponentByDomNode,
	findComponents,
	triggerPublicEvent,
	hasMountedGondelComponent,
} from '@gondel/core';
import { GondelJqueryComponent } from '@gondel/plugin-jquery';
import { getCurrentViewport, VIEWPORT_ENTERED } from '@gondel/plugin-media-queries';
import $ from 'jquery';
import { MainNavigation } from '../../../molecules/main-navigation/js/main-navigation';
import { Overlay, LifecycleHook } from '@swisslife/patterns-base/src/organisms/overlay/js/overlay';
import { SearchBox } from '@swisslife/patterns-base/src/molecules/search-box/js/search-box';
import { Breadcrumb } from '../../../molecules/breadcrumb/js/breadcrumb';
import createFocusTrap, { FocusTrap } from 'focus-trap';
import '../../../molecules/login/js/login';
import '../../../molecules/website-selector/js/website-selector';
import '../../../molecules/main-navigation/js/main-navigation';
import '@swisslife/patterns-base/src/molecules/search-box/js/search-box';
import '@swisslife/patterns-base/src/organisms/overlay/js/overlay';
import eventBus, { SLEvent } from '@swisslife/sl-event-bus/src/sl-event-bus';

import {
	IGHeaderHiddenHandlerOptions,
	IGScrollHandlerOptions,
} from '@swisslife/shared-types/src/types/gondel-public-events';

enum States {
	MainNavOverlayVisible = 'state-o-header--main-nav-overlay-visible',
	MainNavOverlayTransition = 'state-o-header--main-nav-overlay-transition',
	MetaNavOverlayVisible = 'state-o-header--meta-nav-overlay-visible',
	WebsiteSelectorVisible = 'state-o-header--website-selector-visible',
	BurgerActive = 'state-a-burger--active',
	SearchOpen = 'state-o-header--search-open',
	MoodImageBright = 'state-o-header--mood-image-bright',
}

enum Selectors {
	CloseNavigationButton = '.js-o-header__close-button',
	BurgerButton = '.js-a-burger',
	Breadcrumb = '.js-m-breadcrumb',
	LinkSearch = '.js-o-header__search-link',
	FixedWrapper = '.js-o-header__fixed-wrapper',
	OverlaySmall = '.js-o-header__overlay-small',
	SearchOverlay = '.js-o-overlay__search-overlay',
	SearchBox = '.js-m-search-box',
}

enum ScrollDirections {
	up = 'REVERSE',
	down = 'FORWARD',
}

// The @Component decorator will connect the class with `data-g-name="Header"` elements.
@Component('Header')
export class Header extends GondelJqueryComponent {
	private $body: JQuery = $('body');
	private $burgerButton;
	private $breadcrumb;
	private $fixedWrapper;
	private $overlaySmall;
	private mainNavigation;
	private uxBehaviourType;
	private scrollMagicController;
	private trap: FocusTrap;
	private isMoodImageBright: boolean = this.$ctx.hasClass(States.MoodImageBright);
	private shouldHeaderHide: boolean = false;
	private isHeaderVisible: boolean = true;

	public start() {
		const mainNavigationComponents = findComponents<MainNavigation>(this._ctx, 'MainNavigation');
		this.mainNavigation = mainNavigationComponents[0];

		this.$burgerButton = this.$ctx.find(Selectors.BurgerButton);
		this.$fixedWrapper = this.$ctx.find(Selectors.FixedWrapper);
		this.$overlaySmall = this.$ctx.find(Selectors.OverlaySmall);
		this.$breadcrumb = this.$ctx.find(Selectors.Breadcrumb);

		const currentVp = getCurrentViewport();
		this.updateUxBehaviourType(currentVp);

		this.createFocusTrap();

		// event cannot be attached via annotation because keyup event does not bubble
		this.$body.on('keyup', this.handleKeyUp);
	}

	public async sync() {
		this.initializeHeaderAnimation();
		const eb = await eventBus();
		eb.on(SLEvent.ShowMoodImage, this.handleShowMoodImage);
		eb.on(SLEvent.HideMoodImage, this.handleHideMoodImage);
	}

	public stop() {
		this.$body.off('keyup' as any, this.handleKeyUp as any);
	}

	/**
	 * Toggle the provided state class
	 */
	public toggleStateClass(stateToToggle: string, forceState?: boolean) {
		if (forceState !== undefined) {
			this.$ctx.toggleClass(States[stateToToggle], forceState);
		} else {
			this.$ctx.toggleClass(States[stateToToggle]);
		}
	}

	/**
	 * Show search-bar in main-navigation
	 */
	public showSearch() {
		const $overlay = this.$ctx.find(Selectors.SearchOverlay).get(0);
		const overlay = getComponentByDomNode<Overlay>($overlay);
		const $searchBox = this.$ctx.find(`${Selectors.SearchOverlay} ${Selectors.SearchBox}`);

		// temporary disable hide header because sometimes, a scroll event get's triggered when disabling body scroll
		this.shouldHeaderHide = false;

		// search-box is potentially deactivated from onetrust-wrapper
		if (hasMountedGondelComponent($searchBox)) {
			const searchBox = getComponentByDomNode<SearchBox>($searchBox);
			overlay.onLifecycleHook(LifecycleHook.show, () => {
				searchBox.setFocusToInput();
			});
		}

		overlay.onLifecycleHook(LifecycleHook.hide, () => {
			this.closeSearch();
			this.toggleStateClass('MetaNavOverlayVisible', false);
			this.shouldHeaderHide = true;
		});

		overlay.show();
		this.$ctx.addClass(States.SearchOpen);

		this.toggleStateClass('MetaNavOverlayVisible', true);
	}

	/**
	 * Show the overlay
	 * @param overlay {Object}			the overlay to show
	 * @param injectContent {boolean}	if content should be injected
	 * @param $overlayContent {Object}	the overlay content to be injected
	 */
	public showMetaNavOverlay(overlay: Overlay, injectContent?: boolean, $overlayContent?: JQuery) {
		if (injectContent && $overlayContent) {
			overlay.inject($overlayContent, true);
		}

		// temporary disable hide header because sometimes, a scroll event get's triggered when disabling body scroll
		this.shouldHeaderHide = false;

		overlay.onLifecycleHook(LifecycleHook.hide, () => {
			this.toggleStateClass('MetaNavOverlayVisible', false);
			this.shouldHeaderHide = true;
		});
		overlay.show();

		this.toggleStateClass('MetaNavOverlayVisible', true);
	}

	/**
	 * close search-bar in main-navigation
	 */
	public closeSearch() {
		this.$ctx.removeClass(States.SearchOpen);
	}

	public get height(): number {
		return this.$fixedWrapper.height() || 0;
	}

	public get isVisible(): boolean {
		return this.isHeaderVisible;
	}

	public get breadcrumb(): Breadcrumb | undefined {
		if (this.$breadcrumb.length === 0) {
			return undefined;
		}

		return getComponentByDomNode<Breadcrumb>(this.$breadcrumb);
	}

	/**
	 * Handle the event when the user clicks on the close button
	 * @param ev {Event}		click event
	 */
	@EventListener('click', Selectors.CloseNavigationButton)
	public _handleCloseButtonClick(ev: MouseEvent) {
		ev.preventDefault();

		this.closeNavigationOverlay(true);
	}

	/**
	 * Handle the event when the user clicks on the burger button
	 * @param ev {Event}		click event
	 */
	@EventListener('click', Selectors.BurgerButton)
	public _handleBurgerButtonClick(ev: MouseEvent) {
		ev.preventDefault();

		// toggle active class
		this.$burgerButton.toggleClass(States.BurgerActive);

		// set class on the header depending on if the button is active or not
		this.toggleNavigationOverlayStateClass(this.$burgerButton.hasClass(States.BurgerActive));
	}

	/**
	 * Search link (large) click
	 * @param ev {MouseEvent}		native event
	 */
	@EventListener('click', Selectors.LinkSearch)
	public _handleSearchLinkSmallClick(ev: MouseEvent) {
		ev.preventDefault();

		this.showSearch();

		// close a possibly open overlay
		this.closeNavigationOverlay(true);
	}

	@EventListener(VIEWPORT_ENTERED)
	public _handleViewportChangeEvent(event) {
		const previousUxBehaviourType = this.uxBehaviourType;
		this.updateUxBehaviourType(event.viewport);

		// if the viewport change was from small to medium or vice versa, we close a possibly open overlay
		if (previousUxBehaviourType !== this.uxBehaviourType) {
			this.closeNavigationOverlay(false);

			if (this.uxBehaviourType === 'small') {
				this.initializeHeaderAnimation();
			} else {
				this.destroyHeaderAnimation();
				this.$burgerButton.removeClass(States.BurgerActive);
			}
		}
	}

	/**
	 * Handle the event if esc-key is pressed and main-nav-overlay is open.
	 * @param ev {Event}		key event
	 */
	public handleKeyUp = (ev: JQueryKeyEventObject): void => {
		if (this.$ctx.hasClass(States.MainNavOverlayVisible) && ev.keyCode === 27) {
			ev.preventDefault();
			this.closeNavigationOverlay(true);
		}
	};

	/**
	 * Close the navigation overlay
	 */
	public closeNavigationOverlay(withAnimation: boolean) {
		if (this.$ctx.hasClass(States.MainNavOverlayVisible)) {
			this.mainNavigation.resetItemsActiveState(withAnimation);
			this.toggleNavigationOverlayStateClass(false);

			if (this.uxBehaviourType === 'small') {
				this.$burgerButton.removeClass(States.BurgerActive);
			}
		}
	}

	public set moodImageIsBright(value: boolean) {
		if (value) {
			this.$ctx.addClass(States.MoodImageBright);
			return;
		}
		this.$ctx.removeClass(States.MoodImageBright);
	}

	/**
	 * Toggle the navigation overlay state class
	 * @param isVisible {boolean}		if the navigation state class should be applied
	 */
	public toggleNavigationOverlayStateClass(isVisible: boolean) {
		if (isVisible) {
			// temporary disable hide header because sometimes, a scroll event get's triggered when disabling body scroll
			this.shouldHeaderHide = false;

			const eventOptions: IGScrollHandlerOptions = {
				scroll: false,
			};

			triggerPublicEvent('gScrollHandler', this, undefined, eventOptions);
			triggerPublicEvent('gVhCheckHandler', this);
			this.$ctx.addClass(States.MainNavOverlayVisible);

			if (this.uxBehaviourType === 'small') {
				this.$ctx.outerWidth();
				// add transition class to trigger slideDown transition on the navigation overlay
				this.$ctx.addClass(States.MainNavOverlayTransition);

				this.$overlaySmall.one('transitionend', () => {
					this.toggleFocusTrap(true);
				});
			}
		} else {
			this.shouldHeaderHide = true;
			this.toggleFocusTrap(false);

			if (this.uxBehaviourType === 'small') {
				// remove transition class to trigger slideUp transition on the navigation overlay
				this.$ctx.removeClass(States.MainNavOverlayTransition);

				this.$overlaySmall.one('transitionend', () => {
					// remove active class to set navigation overlay to display none again
					this.$ctx.removeClass(States.MainNavOverlayVisible);

					const eventOptions: IGScrollHandlerOptions = {
						scroll: true,
					};

					triggerPublicEvent('gScrollHandler', this, undefined, eventOptions);
				});
			} else {
				const eventOptions: IGScrollHandlerOptions = {
					scroll: true,
				};

				this.$ctx.removeClass(States.MainNavOverlayVisible);
				triggerPublicEvent('gScrollHandler', this, undefined, eventOptions);
			}
		}
	}

	/**
	 * Toggle the focus trap needed for the navigation overlay
	 * @param isActive {boolean}		if the focus trap should be activated
	 */
	public toggleFocusTrap(isActive: boolean) {
		if (isActive) {
			this.trap.activate();
		} else {
			this.trap.deactivate();
		}
	}

	/**
	 * Toggle the website selector on uxBehaviourType small
	 * @param isVisible {boolean}		if the website selector is visible
	 */
	public toggleWebsiteSelector(isVisible: boolean) {
		if (isVisible) {
			this.$ctx.addClass(States.WebsiteSelectorVisible);
		} else {
			this.$ctx.removeClass(States.WebsiteSelectorVisible);
		}
	}

	/**
	 * Scroll the overlay to top on uxBehaviourType small
	 */
	public scrollOverlaySmallToTop() {
		this.$overlaySmall.animate(
			{
				scrollTop: 0,
			},
			350
		);
	}

	private handleShowMoodImage = () => {
		if (!this.isMoodImageBright) {
			this.$ctx.removeClass(States.MoodImageBright);
		}
	};

	private handleHideMoodImage = () => {
		this.$ctx.addClass(States.MoodImageBright);
	};

	/**
	 * Create the focus trap for the navigation overlay
	 */
	private createFocusTrap() {
		this.trap = createFocusTrap(this.$fixedWrapper.get(0), {
			clickOutsideDeactivates: true,
			escapeDeactivates: true,
		});
	}

	/**
	 * Destroy the header animation
	 */
	private destroyHeaderAnimation() {
		this.scrollMagicController.destroy(true);
		this.isHeaderVisible = true;
		triggerPublicEvent('gHeaderHiddenHandler', this, undefined, {
			newState: false,
		} as IGHeaderHiddenHandlerOptions);
	}

	/**
	 * Initialize the header animation
	 */
	private async initializeHeaderAnimation() {
		// tslint:disable
		const {
			ScrollMagic,
		} = (await import(/* webpackChunkName: "dynamic-imports/animation-libs" */ '@swisslife/shared-ui/src/utils/animations/js/animation-libs')) as any;
		// tslint:enable

		this.scrollMagicController = new ScrollMagic.Controller();

		const sceneScrollDirection = new ScrollMagic.Scene().on('update', (e) => {
			const scrollDirection = e.target.controller().info('scrollDirection');

			if (scrollDirection === ScrollDirections.down && this.shouldHeaderHide) {
				this.isHeaderVisible = false;
				triggerPublicEvent('gHeaderHiddenHandler', this, undefined, {
					newState: true,
				} as IGHeaderHiddenHandlerOptions);
			} else {
				this.isHeaderVisible = true;
				triggerPublicEvent('gHeaderHiddenHandler', this, undefined, {
					newState: false,
				} as IGHeaderHiddenHandlerOptions);
			}
		});

		// hide header as soon as the top of the header leaves the top of the screen (100px offset)
		// triggerHook onLeave triggers when the element passes the top of the viewport
		const sceneHideHeader = new ScrollMagic.Scene({
			triggerElement: this.$ctx.get(0),
			triggerHook: 'onLeave',
			offset: 100,
		}).on(
			'enter leave', // event triggers when scrolling down (enter) / up (leave)
			(e) => {
				const scrollDirection = e.target.controller().info('scrollDirection');

				if (scrollDirection === ScrollDirections.down) {
					this.shouldHeaderHide = true;
				} else {
					this.shouldHeaderHide = false;
				}
			}
		);
		// .addIndicators(); // add visual indicators to your page, to be able to see exactly when a scene is triggered;

		// show header as soon as the #footer div enters the screen at the bottom
		// triggerHook onEnter triggers when the element passes the bottom of the viewport
		const sceneShowHeaderBottom = new ScrollMagic.Scene({ triggerElement: '#footer', triggerHook: 'onEnter' }).on(
			'enter leave', // event triggers when scrolling down (enter) / up (leave)
			(e) => {
				const scrollDirection = e.target.controller().info('scrollDirection');

				if (scrollDirection === ScrollDirections.down) {
					this.shouldHeaderHide = false;
					triggerPublicEvent('gHeaderHiddenHandler', this, undefined, {
						newState: false,
					} as IGHeaderHiddenHandlerOptions);
				} else {
					// when we scroll back up again, we set boolean back to true to make hiding again possible
					this.shouldHeaderHide = true;
				}
			}
		);
		// .addIndicators(); // add visual indicators to your page, to be able to see exactly when a scene is triggered;

		// Add Scene to ScrollMagic Controller
		this.scrollMagicController.addScene([sceneScrollDirection, sceneHideHeader, sceneShowHeaderBottom]);
	}

	/**
	 * Update the uxBehaviourType variable
	 * @param viewport {string}		the new viewport
	 */
	private updateUxBehaviourType(viewport: string) {
		if (viewport === 'large' || viewport === 'xlarge' || viewport === 'xxlarge') {
			this.uxBehaviourType = 'large';
		} else {
			this.uxBehaviourType = 'small';
		}
	}
}
